/**
 * @fileoverview Own matter-list discovery and navigation into case detail. Backend records/catalogs supply canonical matters and attributes; browser preferences own columns, sorting, and filters. Selected row data can seed `kc-internal-frontend/src/components/CaseDetails/CaseDetails.js`, which still reloads current matter state. Displayed rows and cached choices do not own matter identity or permission.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../../api';
import './NewMattersTable.css';
import AppIcon from "../../shared/AppIcon";

const CACHE_KEY = 'new_matters_table_cache';
const COLUMNS_KEY = 'new_matters_table_columns';
const STATE_KEY = 'new_matters_table_state';
const CACHE_STALE_MS = 5 * 60 * 1000;

const DEFAULT_COLUMNS = [
{ label: 'Matter Number', value: 'MatterNumber', Type: 'text' },
{ label: 'Attorney', value: 'Attorney', Type: 'text' },
{ label: 'Client', value: 'Client', Type: 'text' },
{ label: 'Defendant', value: 'Defendant', Type: 'text' },
{ label: 'Jurisdiction', value: 'Jurisdiction', Type: 'jurisdiction' },
{ label: 'Date Engaged', value: 'DateEngaged', Type: 'date' },
{ label: 'Stage', value: 'Stage', Type: 'stage' },
{ label: 'Substage', value: 'Substage', Type: 'substage' }];


const DEFAULT_COLUMN_IDS = DEFAULT_COLUMNS.map((column) => column.value);
const MULTI_SELECT_TEXT_FIELDS = new Set(['Attorney']);

const STAGES = [
'Pre-Demand',
'Demand Sent',
'Arbitration-AAA',
'Arbitration-JAMS',
'Arbitration-Other',
'State Court',
'Federal Court',
'Appeal',
'Post Judgment',
'Settled/Resolved',
'Withdrawn'];


const COURT_SUBSTAGES = [
'Complaint Filed',
'Service Complete',
'Motion to Dismiss Filed',
'Discovery',
'Stayed',
'Dispositive Motion Pending',
'Trial Upcoming',
'EMPTY'];


const SETTLED_SUBSTAGES = [
'Settled in Principal',
'Agreement Executed & Payment(s) Pending',
'Payments Complete/Fully Resolved',
'EMPTY'];


const ALL_SUBSTAGES = [
'Complaint Filed',
'Service Complete',
'Motion to Dismiss Filed',
'Discovery',
'Stayed',
'Dispositive Motion Pending',
'Trial Upcoming',
'Appeal',
'Settled in Principal',
'Agreement Executed & Payment(s) Pending',
'Payments Complete/Fully Resolved',
'EMPTY'];


const ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 25, 50, 100];

const DEFAULT_TABLE_STATE = {
  globalSearch: '',
  filters: {},
  activeAttorneysOnly: true,
  sorts: [],
  page: 1,
  rowsPerPage: 25
};



function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}



function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to persist ${key}:`, error);
  }
}



function readCachedPayload() {
  const cached = readJson(CACHE_KEY, null);
  if (!cached || Date.now() - cached.timestamp > CACHE_STALE_MS * 2) {
    return null;
  }
  return cached.data || null;
}



function normalizeColumnType(type) {
  return String(type || 'text').toLowerCase();
}



function normalizeRows(rows) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => ({
    ...row,
    Substage: row.Substage || 'EMPTY'
  }));
}



function normalizeDateKey(value) {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  const stringValue = String(value);
  const isoLike = stringValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoLike) return isoLike[1];

  const parsed = new Date(stringValue);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return stringValue;
}



function formatDate(value) {
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return '';

  const parts = dateKey.split('-');
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  return dateKey;
}



function getCellText(row, column) {
  const value = row[column.value];
  if (normalizeColumnType(column.Type) === 'date') {
    return formatDate(value);
  }
  return value == null ? '' : String(value);
}



function getStageClass(stage) {
  switch (stage) {
    case 'Pre-Demand':
      return 'stage-pre-demand';
    case 'Demand Sent':
      return 'stage-demand-sent';
    case 'Arbitration-AAA':
      return 'stage-arbitration-aaa';
    case 'Arbitration-JAMS':
      return 'stage-arbitration-jams';
    case 'Arbitration-Other':
      return 'stage-arbitration-other';
    case 'State Court':
      return 'stage-state-court';
    case 'Federal Court':
      return 'stage-federal-court';
    case 'Appeal':
      return 'stage-appeal';
    case 'Post Judgment':
      return 'stage-post-judgment';
    case 'Settled/Resolved':
      return 'stage-settled';
    case 'Withdrawn':
      return 'stage-withdrawn';
    default:
      return 'stage-empty';
  }
}



function getSubstageDisplay(row) {
  if (row.Stage === 'State Court' || row.Stage === 'Federal Court') {
    return {
      label: row.Substage || 'EMPTY',
      className: row.Substage === 'EMPTY' ? 'substage-empty' : 'substage-court'
    };
  }

  if (row.Stage === 'Settled/Resolved') {
    return {
      label: row.Substage || 'EMPTY',
      className: row.Substage === 'EMPTY' ? 'substage-empty' : 'substage-settled'
    };
  }

  return {
    label: 'N/A',
    className: 'substage-empty'
  };
}



function getSubstageOptions(stageFilter) {
  const stages = Array.isArray(stageFilter) ? stageFilter : [];
  const includesCourt = stages.includes('State Court') || stages.includes('Federal Court');
  const includesSettled = stages.includes('Settled/Resolved');

  if (includesCourt && !includesSettled) return COURT_SUBSTAGES;
  if (includesSettled && !includesCourt) return SETTLED_SUBSTAGES;
  return ALL_SUBSTAGES;
}



function getUniqueValues(rows, field) {
  return Array.from(new Set(
    rows
    .map((row) => row[field])
    .filter((value) => typeof value === 'string' && value.length > 0)
  )).sort((a, b) => a.localeCompare(b));
}



function getUniqueListValues(rows, field) {
  return Array.from(new Set(
    rows.flatMap((row) => splitListValue(row[field]))
  )).sort((a, b) => a.localeCompare(b));
}


function getActiveAttorneyNames(rows) {
  return new Set(rows.flatMap((row) => {
    const details = row.AttorneyDetails || [];
    return splitListValue(row.Attorney).filter((name) => {
      const attorney = details.find((item) => (item.name || item.Name) === name);
      return (attorney?.isActive ?? attorney?.IsActive) !== false;
    });
  }));
}



function splitListValue(value) {
  if (Array.isArray(value)) {
    return value
    .map((item) => String(item).trim())
    .filter(Boolean);
  }

  if (value == null) return [];

  return String(value)
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);
}



function getInitials(name) {
  if (!name) return '?';
  return String(name)
  .split(/\s+/)
  .filter(Boolean)
  .map((part) => part[0])
  .join('')
  .substring(0, 2)
  .toUpperCase() || '?';
}



function getAttorneyProfilePic(attorney) {
  return attorney?.profilePic || attorney?.ProfilePic || attorney?.attorney_profile_pic || '';
}



function normalizeAttorneyDetails(row) {
  if (Array.isArray(row.AttorneyDetails) && row.AttorneyDetails.length > 0) {
    return row.AttorneyDetails
    .map((attorney, index) => ({
      id: attorney.id ?? attorney.attorney_id ?? `${attorney.name || attorney.Name || index}`,
      name: attorney.name || attorney.Name || attorney.attorney_name || '',
      profilePic: getAttorneyProfilePic(attorney),
      isPrimary: Boolean(attorney.isPrimary ?? attorney.IsPrimary ?? attorney.is_primary)
    }))
    .filter((attorney) => attorney.name);
  }

  return splitListValue(row.Attorney).map((name, index) => ({
    id: `${name}-${index}`,
    name,
    profilePic: '',
    isPrimary: false
  }));
}



function buildColumns(attributeTypes) {
  const attributes = Array.isArray(attributeTypes) ?
  attributeTypes
  .filter((attribute) => attribute && attribute.Attribute)
  .map((attribute) => ({
    label: attribute.Attribute,
    value: attribute.Attribute,
    Type: attribute.Type || 'text'
  })) :
  [];

  const deduped = new Map();
  DEFAULT_COLUMNS.concat(attributes).forEach((column) => {
    if (!deduped.has(column.value)) {
      deduped.set(column.value, column);
    }
  });
  return Array.from(deduped.values());
}



function compareValues(a, b, column) {
  const type = normalizeColumnType(column?.Type);
  const aMissing = a == null || a === '';
  const bMissing = b == null || b === '';

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (type === 'number') {
    const aNumber = Number(a);
    const bNumber = Number(b);
    if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
      return aNumber - bNumber;
    }
  }

  if (type === 'date') {
    return normalizeDateKey(a).localeCompare(normalizeDateKey(b));
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base'
  });
}



function filterRows(rows, columns, visibleColumns, state) {
  const filters = state.filters || {};
  const columnByValue = new Map(columns.map((column) => [column.value, column]));
  const globalSearch = String(state.globalSearch || '').trim().toLowerCase();

  return rows.filter(

  (row) => {
    for (const [field, rawFilter] of Object.entries(filters)) {
      const column = columnByValue.get(field);
      if (!column) continue;

      const type = normalizeColumnType(column.Type);
      const value = row[field];

      if (Array.isArray(rawFilter)) {
        const rowValues = MULTI_SELECT_TEXT_FIELDS.has(field) ?
        splitListValue(value) :
        [value || 'EMPTY'];
        const matchesSelectedValue = rawFilter.some((filterValue) => rowValues.includes(filterValue));

        if (rawFilter.length > 0 && !matchesSelectedValue) {
          return false;
        }
        continue;
      }

      const filterValue = String(rawFilter || '').trim();
      if (!filterValue) continue;

      if (type === 'number') {
        const rowNumber = Number(value);
        const filterNumber = Number(filterValue);
        if (Number.isNaN(rowNumber) || Number.isNaN(filterNumber) || rowNumber !== filterNumber) {
          return false;
        }
      } else if (type === 'date') {
        if (normalizeDateKey(value) !== normalizeDateKey(filterValue)) {
          return false;
        }
      } else {
        const cellValue = value == null ? '' : String(value);
        if (!cellValue.toLowerCase().includes(filterValue.toLowerCase())) {
          return false;
        }
      }
    }

    if (!globalSearch) return true;

    return visibleColumns.some((column) =>
    getCellText(row, column).toLowerCase().includes(globalSearch)
    );
  });
}



function sortRows(rows, columns, sorts) {
  if (!Array.isArray(sorts) || sorts.length === 0) return rows;

  const columnByValue = new Map(columns.map((column) => [column.value, column]));

  return rows
  .map((row, index) => ({ row, index }))
  .sort((left, right) => {
    for (const sort of sorts) {
      const column = columnByValue.get(sort.field);
      const direction = sort.direction === 'desc' ? -1 : 1;
      const comparison = compareValues(left.row[sort.field], right.row[sort.field], column);
      if (comparison !== 0) return comparison * direction;
    }
    return left.index - right.index;
  })
  .map((item) => item.row);
}



function csvEscape(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}



function buildCsv(rows, columns) {
  const header = columns.map((column) => csvEscape(column.label)).join(',');
  const body = rows.map((row) =>
  columns.map((column) => csvEscape(getCellText(row, column))).join(',')
  );
  return [header].concat(body).join('\n');
}



function downloadCsv(csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const supportsObjectUrl = window.URL && typeof window.URL.createObjectURL === 'function';
  const url = supportsObjectUrl ?
  window.URL.createObjectURL(blob) :
  `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
  const link = document.createElement('a');

  link.href = url;
  link.download = 'new-matters-table.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (supportsObjectUrl && typeof window.URL.revokeObjectURL === 'function') {
    window.URL.revokeObjectURL(url);
  }
}



function NewMattersTable() {
  const navigate = useNavigate();
  const [matters, setMatters] = useState([]);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mattersLoaded, setMattersLoaded] = useState(false);
  const [error, setError] = useState('');
  const [attributeError, setAttributeError] = useState('');
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(() => {
    const stored = readJson(COLUMNS_KEY, DEFAULT_COLUMN_IDS);
    return Array.isArray(stored) && stored.length > 0 ? stored : DEFAULT_COLUMN_IDS;
  });
  const [tableState, setTableState] = useState(() => {
    const stored = readJson(STATE_KEY, DEFAULT_TABLE_STATE);
    return {
      ...DEFAULT_TABLE_STATE,
      ...stored,
      activeAttorneysOnly: stored?.activeAttorneysOnly ?? !stored?.filters?.Attorney?.length,
      filters: stored?.filters || {},
      sorts: Array.isArray(stored?.sorts) ? stored.sorts : []
    };
  });

  const allColumns = useMemo(() => buildColumns(attributeTypes), [attributeTypes]);
  const visibleColumns = useMemo(() => {
    const visible = allColumns.filter((column) => visibleColumnIds.includes(column.value));
    return visible.length > 0 ? visible : DEFAULT_COLUMNS;
  }, [allColumns, visibleColumnIds]);

  const jurisdictionOptions = useMemo(() => getUniqueValues(matters, 'Jurisdiction'), [matters]);
  const activeAttorneyNames = useMemo(() => getActiveAttorneyNames(matters), [matters]);
  const attorneyOptions = useMemo(() => {
    const names = getUniqueListValues(matters, 'Attorney');
    return tableState.activeAttorneysOnly ? names.filter((name) => activeAttorneyNames.has(name)) : names;
  }, [matters, activeAttorneyNames, tableState.activeAttorneysOnly]);
  const substageOptions = useMemo(
    () => getSubstageOptions(tableState.filters?.Stage),
    [tableState.filters]
  );

  const filteredRows = useMemo(
    () => filterRows(matters, allColumns, visibleColumns, tableState),
    [matters, allColumns, visibleColumns, tableState]
  );
  const sortedRows = useMemo(
    () => sortRows(filteredRows, allColumns, tableState.sorts),
    [filteredRows, allColumns, tableState.sorts]
  );

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / tableState.rowsPerPage));
  const page = Math.min(Math.max(1, tableState.page), totalPages);
  const firstRow = sortedRows.length === 0 ? 0 : (page - 1) * tableState.rowsPerPage + 1;
  const lastRow = Math.min(page * tableState.rowsPerPage, sortedRows.length);
  const pagedRows = sortedRows.slice((page - 1) * tableState.rowsPerPage, page * tableState.rowsPerPage);

  const loadData = useCallback(

  async () => {
    document.title = 'Internal';
    setError('');
    setAttributeError('');

    const cached = readCachedPayload();
    if (cached) {
      setMatters(normalizeRows(cached.matters));
      setAttributeTypes(Array.isArray(cached.attributeTypes) ? cached.attributeTypes : []);
      setMattersLoaded(true);
      setLoading(false);
    } else {
      setLoading(true);
    }

    api.get('/matters')
    .then((response) => {
      setMatters(normalizeRows(response.data));
      setMattersLoaded(true);
      setLoading(false);
    })
    .catch((requestError) => {
      console.error(requestError);
      if (!cached) {
        setError('Unable to load matters.');
        setLoading(false);
      }
    });

    api.get('/case_attribute_types')
    .then((response) => {
      setAttributeTypes(Array.isArray(response.data) ? response.data : []);
    })
    .catch((requestError) => {
      console.error(requestError);
      setAttributeError('Attribute columns could not be loaded.');
    });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (mattersLoaded) {
      writeJson(CACHE_KEY, {
        timestamp: Date.now(),
        data: {
          matters,
          attributeTypes
        }
      });
    }
  }, [matters, attributeTypes, mattersLoaded]);

  useEffect(() => {
    writeJson(COLUMNS_KEY, visibleColumnIds);
  }, [visibleColumnIds]);

  useEffect(() => {
    writeJson(STATE_KEY, tableState);
  }, [tableState]);

  useEffect(() => {
    if (page !== tableState.page) {
      setTableState((current) => ({ ...current, page }));
    }
  }, [page, tableState.page]);

  const updateTableState =

  (updates, resetPage = true) => {
    setTableState((current) => ({
      ...current,
      ...updates,
      page: resetPage ? 1 : updates.page || current.page
    }));
  };

  const updateFilter =

  (field, value) => {
    setTableState(

    (current) => {
      const filters = { ...(current.filters || {}) };
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isEmptyValue = !Array.isArray(value) && String(value || '').trim() === '';

      if (isEmptyArray || isEmptyValue) {
        delete filters[field];
      } else {
        filters[field] = value;
      }

      if (field === 'Stage') {
        const allowedSubstages = getSubstageOptions(filters.Stage);
        if (Array.isArray(filters.Substage)) {
          const nextSubstages = filters.Substage.filter((substage) => allowedSubstages.includes(substage));
          if (nextSubstages.length > 0) {
            filters.Substage = nextSubstages;
          } else {
            delete filters.Substage;
          }
        }
      }

      return {
        ...current,
        filters,
        page: 1
      };
    });
  };

  const updateActiveAttorneysOnly = (activeAttorneysOnly) => {
    setTableState((current) => {
      const filters = { ...current.filters };
      if (activeAttorneysOnly && Array.isArray(filters.Attorney)) {
        filters.Attorney = filters.Attorney.filter((name) => activeAttorneyNames.has(name));
        if (filters.Attorney.length === 0) delete filters.Attorney;
      }
      return { ...current, filters, activeAttorneysOnly, page: 1 };
    });
  };

  const removeFilter =

  (field) => {
    setTableState((current) => {
      const filters = { ...(current.filters || {}) };
      delete filters[field];
      return {
        ...current,
        filters,
        page: 1
      };
    });
  };

  const clearState =

  () => {
    localStorage.removeItem(STATE_KEY);
    setTableState(DEFAULT_TABLE_STATE);
  };

  const toggleColumn =

  (columnId) => {
    setVisibleColumnIds((current) => {
      if (current.includes(columnId)) {
        return current.filter((id) => id !== columnId);
      }
      return current.concat(columnId);
    });
  };

  const handleSort =

  (field, addSort) => {
    setTableState(

    (current) => {
      const existing = current.sorts.find((sort) => sort.field === field);
      let nextSort;

      if (!existing) {
        nextSort = { field, direction: 'asc' };
      } else if (existing.direction === 'asc') {
        nextSort = { field, direction: 'desc' };
      } else {
        nextSort = null;
      }

      let sorts;
      if (addSort) {
        sorts = current.sorts.filter((sort) => sort.field !== field);
        if (nextSort) sorts.push(nextSort);
      } else {
        sorts = nextSort ? [nextSort] : [];
      }

      return {
        ...current,
        sorts,
        page: 1
      };
    });
  };

  const getSortLabel =

  (field) => {
    const index = tableState.sorts.findIndex((sort) => sort.field === field);
    if (index < 0) return 'Unsorted';
    const sort = tableState.sorts[index];
    return `${sort.direction === 'desc' ? 'Descending' : 'Ascending'} ${index + 1}`;
  };

  const getSortIconClass =

  (field) => {
    const sort = tableState.sorts.find((item) => item.field === field);
    if (!sort) return 'pi pi-sort-alt';
    return sort.direction === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up';
  };

  const getSortDirection =

  (field) =>
    tableState.sorts.find((item) => item.field === field)?.direction || null;

  const exportCsv =

  () => {
    downloadCsv(buildCsv(sortedRows, visibleColumns));
  };

  const goToMatter =

  (matter) => {
    if (!matter.MatterNumber) return;
    navigate(`/case_details/${matter.MatterNumber}`, {
      state: {
        matterData: matter
      }
    });
  };

  const activeFilterEntries = Object.entries(tableState.filters || {})
  .filter(([, value]) => Array.isArray(value) ? value.length > 0 : String(value || '').trim() !== '');

  return (
    <div className="new-matters-page">
            <section className="new-matters-shell" aria-label="New matters table">
                <div className="new-matters-toolbar">
                    <div className="new-matters-toolbar__primary">
                        <div className="new-matters-title">
                            <span className="new-matters-title__label">Matters</span>
                            <span className="new-matters-title__count">{sortedRows.length} shown</span>
                        </div>

                        <label className="new-matters-search">
                            <span className="new-matters-sr-only">Search all visible columns</span>
                            <input
                type="search"
                value={tableState.globalSearch}
                onChange={(event) => updateTableState({ globalSearch: event.target.value })}
                placeholder="Search visible columns"
                aria-label="Search visible columns" />

                        </label>
                    </div>

                    {!loading && !error &&
          <div className="new-matters-paginator" aria-label="Table pagination">
                            <label className="new-matters-paginator__rows">
                                Rows
                                <select
                value={tableState.rowsPerPage}
                onChange={(event) => updateTableState({ rowsPerPage: Number(event.target.value) })}>

                                    {ROWS_PER_PAGE_OPTIONS.map((option) =>
                <option key={option} value={option}>{option}</option>
                )}
                                </select>
                            </label>
                            <span className="new-matters-paginator__range">{firstRow}-{lastRow} of {sortedRows.length}</span>
                            <div className="new-matters-paginator__buttons">
                                <button
                type="button"
                className="new-matters-icon-button"
                onClick={() => updateTableState({ page: page - 1 }, false)}
                disabled={page <= 1}
                aria-label="Previous page">

                                    <AppIcon icon="pi pi-chevron-left" aria-hidden="true" />
                                </button>
                                <button
                type="button"
                className="new-matters-icon-button"
                onClick={() => updateTableState({ page: page + 1 }, false)}
                disabled={page >= totalPages}
                aria-label="Next page">

                                    <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
                                </button>
                            </div>
                        </div>}


                    <div className="new-matters-actions">
                        <div className="new-matters-menu-wrap">
                            <button
                type="button"
                className="new-matters-button"
                onClick={() => setColumnMenuOpen((open) => !open)}
                aria-expanded={columnMenuOpen}
                aria-label="Columns">

                                <AppIcon icon="pi pi-table" aria-hidden="true" />
                                <span>Columns</span>
                            </button>
                            {columnMenuOpen &&
              <div className="new-matters-popover new-matters-column-menu" role="dialog" aria-label="Select columns">
                                    <div className="new-matters-popover__title">Select columns</div>
                                    {allColumns.map((column) =>
                <label key={column.value} className="new-matters-checkbox-row">
                                            <input
                    type="checkbox"
                    checked={visibleColumnIds.includes(column.value)}
                    onChange={() => toggleColumn(column.value)} />

                                            <span>{column.label}</span>
                                        </label>
                )}
                                </div>}

                        </div>

                        <button type="button" className="new-matters-button" onClick={exportCsv} aria-label="Export CSV">
                            <AppIcon icon="pi pi-download" aria-hidden="true" />
                            <span>Export CSV</span>
                        </button>
                        <button type="button" className="new-matters-button" onClick={clearState} aria-label="Clear">
                            <AppIcon icon="pi pi-filter-slash" aria-hidden="true" />
                            <span>Clear</span>
                        </button>
                    </div>
                </div>

                <div className="new-matters-filter-chips" aria-label="Active filters">
                    {!tableState.globalSearch && activeFilterEntries.length === 0 &&
          <span className="new-matters-filter-chips__empty">No active filters</span>}

                    {tableState.globalSearch &&
          <button
            type="button"
            className="new-matters-chip"
            onClick={() => updateTableState({ globalSearch: '' })}>

                            <span>Search: {tableState.globalSearch}</span>
                            <AppIcon icon="pi pi-times" aria-hidden="true" />
                        </button>}

                    {activeFilterEntries.map(

                    ([field, value]) => {
            const column = allColumns.find((item) => item.value === field);
            const label = column?.label || field;
            const displayValue = Array.isArray(value) ? value.join(', ') : value;
            return (
              <button
                type="button"
                className="new-matters-chip"
                key={field}
                onClick={() => removeFilter(field)}>

                                <span>{label}: {displayValue}</span>
                                <AppIcon icon="pi pi-times" aria-hidden="true" />
                            </button>);

          })}
                </div>

                {attributeError &&
        <div className="new-matters-inline-alert" role="status">{attributeError}</div>}


                {loading &&
        <div className="new-matters-state" role="status">Loading matters...</div>}


                {!loading && error &&
        <div className="new-matters-state new-matters-state--error" role="alert">
                        <span>{error}</span>
                        <button type="button" className="new-matters-button" onClick={loadData}>
                            Retry
                        </button>
                    </div>}


                {!loading && !error &&
        <>
                        <div className="new-matters-table-wrap">
                            <table className="new-matters-table">
                                <thead>
                                    <tr>
                                        {visibleColumns.map((column) => {
                  const sortDirection = getSortDirection(column.value);
                  return <th
                    key={column.value}
                    className={sortDirection ? 'new-matters-th--sorted' : undefined}
                    aria-sort={sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : 'none'}>
                                                <div className="new-matters-th">
                                                    <button
                        type="button"
                        className="new-matters-sort-button"
                        onClick={(event) => handleSort(column.value, event.shiftKey)}
                        aria-label={`Sort ${column.label}`}>

                                                        <span className="new-matters-sort-label">{column.label}</span>
                                                        <span className="new-matters-sort-meta">
                                                            <AppIcon icon={getSortIconClass(column.value)} aria-hidden="true" />
                                                            <span>{getSortLabel(column.value)}</span>
                                                        </span>
                                                    </button>
                                                    <ColumnFilterControl
                        column={column}
                        value={tableState.filters?.[column.value]}
                        attorneyOptions={attorneyOptions}
                        activeAttorneysOnly={tableState.activeAttorneysOnly}
                        onActiveAttorneysOnlyChange={updateActiveAttorneysOnly}
                        jurisdictionOptions={jurisdictionOptions}
                        substageOptions={substageOptions}
                        onChange={(value) => updateFilter(column.value, value)} />

                                                </div>
                                            </th>;
                  })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedRows.length === 0 &&
                <tr>
                                            <td colSpan={visibleColumns.length || 1} className="new-matters-empty">
                                                No matters found.
                                            </td>
                                        </tr>}

                                    {pagedRows.map((matter) =>
                <tr
                  key={matter.MatterNumber}
                  data-testid="new-matters-row"
                  tabIndex={0}
                  onClick={() => goToMatter(matter)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      goToMatter(matter);
                    }
                  }}>

                                            {visibleColumns.map((column) =>
                  <td key={column.value}>
                                                    <CellValue row={matter} column={column} />
                                                </td>
                  )}
                                        </tr>
                )}
                                </tbody>
                            </table>
                        </div>
                    </>}

            </section>
        </div>);

}



function ColumnFilterControl({
  column,
  value,
  attorneyOptions,
  activeAttorneysOnly,
  onActiveAttorneysOnlyChange,
  jurisdictionOptions,
  substageOptions,
  onChange
}) {
  const type = normalizeColumnType(column.Type);

  if (MULTI_SELECT_TEXT_FIELDS.has(column.value) || type === 'jurisdiction' || type === 'stage' || type === 'substage') {
    const options = column.value === 'Attorney' ?
    attorneyOptions :
    type === 'jurisdiction' ?
    jurisdictionOptions :
    type === 'stage' ?
    STAGES :
    substageOptions;

    return (
      <HeaderMultiSelectFilter
        label={column.label}
        values={value}
        options={options}
        showSelectAll={type === 'stage'}
        activeAttorneysOnly={column.value === 'Attorney' ? activeAttorneysOnly : undefined}
        onActiveAttorneysOnlyChange={onActiveAttorneysOnlyChange}
        onChange={onChange} />);


  }

  return (
    <input
      className="new-matters-column-filter-input"
      type={type === 'date' ? 'date' : type === 'number' ? 'number' : 'text'}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={`Filter ${column.label}`}
      aria-label={`Filter ${column.label}`} />);


}



function HeaderMultiSelectFilter({ label, values, options, showSelectAll = false, activeAttorneysOnly, onActiveAttorneysOnlyChange, onChange }) {
  const selectedValues = Array.isArray(values) ? values : [];
  const [optionSearch, setOptionSearch] = useState('');
  const summary = selectedValues.length > 0 ? `${selectedValues.length} selected` : 'Any';
  const normalizedSearch = optionSearch.trim().toLowerCase();
  const filteredOptions = normalizedSearch ?
  options.filter((option) => option.toLowerCase().includes(normalizedSearch)) :
  options;

  useEffect(() => {
    if (!Array.isArray(values) || values.length === 0) {
      setOptionSearch('');
    }
  }, [values]);

  const toggleValue =

  (option) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((value) => value !== option));
    } else {
      onChange(selectedValues.concat(option));
    }
  };

  const selectAllOptions =

  () => {
    onChange(options);
  };

  return (
    <details className="new-matters-header-filter">
            <summary aria-label={`Filter ${label}`}>
                <span>{summary}</span>
                <AppIcon icon="pi pi-chevron-down" aria-hidden="true" />
            </summary>
            <div className="new-matters-header-filter__menu" role="group" aria-label={`${label} options`}>
                {activeAttorneysOnly !== undefined &&
        <label className="new-matters-header-filter__option new-matters-header-filter__active-only">
                    <input
            type="checkbox"
            checked={activeAttorneysOnly}
            onChange={(event) => onActiveAttorneysOnlyChange(event.target.checked)} />
                    <span>Active attorneys only</span>
                </label>}
                <label className="new-matters-header-filter__search">
                    <span className="new-matters-sr-only">Search {label} options</span>
                    <input
            type="search"
            value={optionSearch}
            onChange={(event) => setOptionSearch(event.target.value)}
            placeholder={`Search ${label}`}
            aria-label={`Search ${label} options`} />

                </label>
                {showSelectAll && options.length > 0 &&
        <button
          type="button"
          className="new-matters-header-filter__select-all"
          onClick={selectAllOptions}
          disabled={selectedValues.length === options.length}
          aria-label={`Select all ${label} options`}>

                        Select all
                    </button>}

                {options.length === 0 &&
        <div className="new-matters-header-filter__empty">No options</div>}

                {options.length > 0 && filteredOptions.length === 0 &&
        <div className="new-matters-header-filter__empty">No matches</div>}

                {filteredOptions.map((option) =>
        <label key={option} className="new-matters-header-filter__option">
                        <input
            type="checkbox"
            checked={selectedValues.includes(option)}
            onChange={() => toggleValue(option)}
            aria-label={`${label} ${option}`} />

                        <span aria-hidden="true">{option}</span>
                    </label>
        )}
            </div>
        </details>);

}



function CellValue({ row, column }) {
  const type = normalizeColumnType(column.Type);

  if (column.value === 'Attorney') {
    return <AttorneyChips row={row} />;
  }

  if (type === 'stage') {
    const value = row[column.value] || '';
    return <span className={`new-matters-badge ${getStageClass(value)}`}>{value}</span>;
  }

  if (type === 'substage') {
    const display = getSubstageDisplay(row);
    return <span className={`new-matters-badge ${display.className}`}>{display.label}</span>;
  }

  if (type === 'date') {
    return <span className="app-data-value">{formatDate(row[column.value])}</span>;
  }

  if (column.value === 'MatterNumber') {
    return <span className="app-data-value">{row[column.value] == null ? '' : String(row[column.value])}</span>;
  }

  return <span>{row[column.value] == null ? '' : String(row[column.value])}</span>;
}



function AttorneyChips({ row }) {
  const attorneys = normalizeAttorneyDetails(row);
  if (attorneys.length === 0) return <span />;

  return (
    <div className="new-matters-attorney-chips" aria-label={`Attorneys: ${attorneys.map((attorney) => attorney.name).join(', ')}`}>
            {attorneys.map((attorney, index) =>
      <AttorneyChip attorney={attorney} key={`${attorney.id}-${index}`} />
      )}
        </div>);

}



function AttorneyChip({ attorney }) {
  const [imageFailed, setImageFailed] = useState(false);
  const profilePic = getAttorneyProfilePic(attorney);
  const showImage = profilePic && !imageFailed;

  return (
    <span
      className="new-matters-attorney-chip"
      title={attorney.name}>

            {showImage ?
      <img
        className="new-matters-attorney-chip__avatar"
        src={profilePic}
        alt={attorney.name}
        referrerPolicy="no-referrer"
        crossOrigin="anonymous"
        onError={() => setImageFailed(true)} /> :

      <span className="new-matters-attorney-chip__avatar new-matters-attorney-chip__avatar--initials" aria-hidden="true">
                    {getInitials(attorney.name)}
                </span>}

            <span className="new-matters-attorney-chip__name">{attorney.name}</span>
        </span>);

}

export default NewMattersTable;
